import { memo } from 'react';
import { Clock, MapPin, Star, Utensils, Footprints } from 'lucide-react';
import { formatTime, formatDuration, getTypeConfig } from './helpers';

/**
 * Renders a single timeline item (ride, show, dining, transfer, etc.)
 * from a V3 day.items[] entry.
 */
function ItemRow({ item, compact = false }) {
  const { name, type, startMin, endMin, durationMin, zone, isTransfer, meta } = item;
  const cfg = getTypeConfig(type);
  const isMustDo = meta?.mustDo;
  const isStructural = type === 'buffer' || type === 'transfer';
  const isDining = type === 'dining';
  const isFreeTime = type === 'open_time' || type === 'flexible_time';

  // Compact structural items (short buffers, transfers)
  if (isStructural && compact) {
    return (
      <div className="flex gap-2 items-center px-3 py-1 text-xs text-gray-400">
        {isTransfer ? (
          <>
            <Footprints className="w-3 h-3" />
            <span>{name}</span>
            {durationMin > 0 && <span className="text-gray-300">({formatDuration(durationMin)})</span>}
          </>
        ) : (
          <>
            <span className="w-1 h-1 bg-gray-300 rounded-full" />
            <span>{name}</span>
          </>
        )}
      </div>
    );
  }

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 rounded-lg border transition-colors ${
        isStructural
          ? 'border-gray-100 bg-gray-50/50'
          : isFreeTime
          ? 'border-dashed border-gray-200 bg-white'
          : 'border-gray-200 bg-white hover:border-gray-300'
      }`}
    >
      {/* Time column */}
      <div className="flex-shrink-0 w-16 pt-0.5">
        {startMin != null && (
          <span className="text-xs font-medium text-gray-900">{formatTime(startMin)}</span>
        )}
        {durationMin > 0 && !isStructural && (
          <div className="text-[10px] text-gray-400">{formatDuration(durationMin)}</div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex gap-2 items-center">
          <span
            className={`text-sm font-medium ${
              isStructural ? 'text-gray-400' : 'text-gray-900'
            }`}
          >
            {name}
          </span>
          {isMustDo && (
            <Star className="flex-shrink-0 w-3 h-3 text-amber-500 fill-amber-500" />
          )}
        </div>

        {/* Zone / location */}
        {zone && !isStructural && (
          <div className="flex gap-1 items-center mt-0.5">
            <MapPin className="w-3 h-3 text-gray-400" />
            <span className="text-xs text-gray-500">{zone}</span>
          </div>
        )}

        {/* Dining meal type */}
        {isDining && meta?.mealType && (
          <div className="flex gap-1 items-center mt-0.5">
            <Utensils className="w-3 h-3 text-gray-400" />
            <span className="text-xs text-gray-500 capitalize">{meta.mealType}</span>
            {meta?.venueAreaType && (
              <span className="text-xs text-gray-400">
                ({meta.venueAreaType === 'inPark' ? 'In Park' : meta.venueAreaType})
              </span>
            )}
          </div>
        )}

        {/* Free time suggestions */}
        {isFreeTime && meta?.decisionPrompt && (
          <p className="mt-0.5 text-xs italic text-gray-400">{meta.decisionPrompt}</p>
        )}

        {/* Transfer details */}
        {isTransfer && meta?.fromZone && meta?.toZone && (
          <div className="flex gap-1 items-center mt-0.5 text-xs text-gray-400">
            <Footprints className="w-3 h-3" />
            <span>{meta.fromZone} &rarr; {meta.toZone}</span>
            {meta.walkMin > 0 && <span>({meta.walkMin} min walk)</span>}
          </div>
        )}
      </div>

      {/* Type badge */}
      <div className="flex-shrink-0">
        <span
          className={`inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-full border ${cfg.style}`}
        >
          {cfg.label}
        </span>
      </div>
    </div>
  );
}

export default memo(ItemRow);
